// Who may come in, and how a signed-in teacher proves it is still them (#566,
// #567).
//
// Pure, and mirrored into functions/domain/ like everything else here, because
// the same question is asked in two places and the answer has to be the same
// one: the web app asks it before it draws anything (src/App.jsx `SignInGate`),
// and the device-link callables ask it before they hand a phone a session
// (functions/devicelink/link.js). The phones ask it too, after an
// email-and-password sign-in (Android `EmailSignIn.mayEnter`, and the
// iPhone's), and refuse and sign out an unconfirmed address (#725). It is not
// in the Rust core: the whole rule is "the flag is true", which a second copy
// cannot get wrong in a way a shared one would prevent.
//
// ── The rule ───────────────────────────────────────────────────────────────
//
// **An account may use the app once Firebase says its email address is
// verified, and not before** (#566). A teacher who signed up with an email and
// a password sees a check-your-email screen and nothing else until they have
// clicked the link Firebase sent.
//
// It reads `emailVerified` and nothing else — deliberately not the provider.
// A Google sign-in is let straight in because Google vouches for the address
// and Firebase records that as `emailVerified: true` (#567), not because its
// provider id is `google.com`. Keying on the provider would let in any future
// provider that does *not* vouch for its address, and would keep out a
// password account that has been verified, which is the ordinary case.
//
// ── Existing accounts ──────────────────────────────────────────────────────
//
// However old it is, an email/password account that never clicked its link
// sees the check-your-email screen on its next visit, with Resend; its studio
// is untouched and one click lets it back in.
//
// ── Where it is enforced ───────────────────────────────────────────────────
//
// In the web client, and at the two device-link callables — and **not** in
// firestore.rules. Verification is a statement about an address, not a
// permission on a studio's own data: an unverified account can reach only its
// own tree under `users/{uid}`. Mail through our SendGrid domain, what an
// unverified address could be used for, is refused on the server
// (functions/billing/entitled.js `senderMayDeliver`). A rule would cut off
// phones an unverified account had already linked, depend on an ID token that
// says `false` for up to an hour after the link is clicked, and refuse every
// test suite that signs up an unverified user. Phones already linked to an
// unverified account keep working; they cannot be re-linked until the address
// is confirmed.

/**
 * Whether this Firebase user may use the app.
 *
 * Takes anything with an `emailVerified` field: the client SDK's `User`, the
 * admin SDK's `UserRecord`, or a plain object in a test. A missing user, or a
 * flag that is anything but literally `true`, is a no.
 */
export function accountMayEnter(user) {
  return user?.emailVerified === true;
}

/**
 * What the web app draws for the session it has.
 *
 *   'loading'      Firebase has not answered yet. Nothing is drawn, so a
 *                  verified teacher never sees the gate flash on a reload.
 *   'signed-out'   Nobody is signed in; the router sends them to /login.
 *   'verify-email' Signed in, address not confirmed: the check-your-email
 *                  screen, and nothing else.
 *   'open'         The app.
 *
 * `emailVerified` is passed beside `user` rather than read off it, because
 * `user.reload()` changes the flag on the same object and AuthContext holds
 * it separately so that React notices (see the comment there).
 */
export function signInGate({ loading, user, emailVerified }) {
  if (loading) return 'loading';
  if (!user) return 'signed-out';
  return accountMayEnter({ emailVerified }) ? 'open' : 'verify-email';
}

/**
 * How a signed-in teacher proves it is still them before something
 * irreversible — Delete Account (#432).
 *
 * `providerIds` is the list of `providerId`s in the user's `providerData`.
 * A password wins when there is one, because the deletion form asks for it
 * and it needs no pop-up. An account with Google and no password
 * re-authenticates with Google. Anything else has no way to prove it here, and
 * says so rather than asking for a password that does not exist.
 */
export function reauthMethod(providerIds) {
  const ids = Array.isArray(providerIds) ? providerIds : [];
  if (ids.includes('password')) return 'password';
  if (ids.includes('google.com')) return 'google';
  return 'none';
}

/**
 * What the device-link callables say to an account that has not confirmed its
 * address. The web app's own gate means a teacher should never read it: it is
 * for a session that reached the callable some other way.
 */
export const UNVERIFIED_DEVICE_LINK =
  'Confirm your email address first: open the link we emailed you, then sign in on the website again.';
